import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { formatTimeInBrussels } from '../../utils/format';

interface Position {
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
}

interface RouteMapProps {
  positions: Position[];
  loading?: boolean;
}

export function RouteMap({ positions, loading = false }: RouteMapProps) {
  const mapRef = useRef<L.Map | null>(null);

  // Fit bounds to show entire route
  useEffect(() => {
    if (!mapRef.current || positions.length < 2 || loading) return;

    const latlngs = positions.map((pos) => [pos.latitude, pos.longitude] as [number, number]);
    const bounds = L.latLngBounds(latlngs);

    // Fit with padding
    mapRef.current.fitBounds(bounds, { padding: [50, 50] });
  }, [positions, loading]);

  if (!positions || positions.length === 0) {
    return (
      <div className="w-full h-96 bg-gray-100 flex items-center justify-center rounded-lg">
        <p className="text-gray-500">Aucune position disponible</p>
      </div>
    );
  }

  // Route coordinates
  const routeCoordinates = positions.map((pos) => [pos.latitude, pos.longitude] as [number, number]);

  // Start and end positions
  const startPosition = positions[0];
  const endPosition = positions[positions.length - 1];

  return (
    <div className="w-full h-96 rounded-lg overflow-hidden shadow-md">
      <MapContainer
        ref={mapRef}
        center={[startPosition.latitude, startPosition.longitude]}
        zoom={13}
        className="w-full h-full"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap contributors'
        />

        {/* Route polyline */}
        <Polyline
          positions={routeCoordinates}
          color="#3b82f6"
          weight={3}
          opacity={0.8}
          dashArray="5, 5"
        />

        {/* Start marker */}
        <Marker
          position={[startPosition.latitude, startPosition.longitude]}
          icon={L.icon({
            iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIxNiIgY3k9IjE2IiByPSIxMiIgZmlsbD0iIzEwYjk4MSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuMzVlbSIgZm9udC1zaXplPSIxNiIgZmlsbD0id2hpdGUiIGZvbnQtd2VpZ2h0PSJib2xkIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiPkE8L3RleHQ+PC9zdmc+Cg==',
            iconSize: [32, 32],
            iconAnchor: [16, 16],
            popupAnchor: [0, -16],
            className: 'drop-shadow-lg',
          })}
        >
          <Popup>
            <div className="text-sm">
              <p className="font-semibold">Départ</p>
              <p className="text-gray-600">{formatTimeInBrussels(startPosition.timestamp)}</p>
              <p className="text-xs text-gray-500">
                {startPosition.latitude.toFixed(4)}, {startPosition.longitude.toFixed(4)}
              </p>
            </div>
          </Popup>
        </Marker>

        {/* End marker */}
        <Marker
          position={[endPosition.latitude, endPosition.longitude]}
          icon={L.icon({
            iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIxNiIgY3k9IjE2IiByPSIxMiIgZmlsbD0iI2VmNDQ0NCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuMzVlbSIgZm9udC1zaXplPSIxNiIgZmlsbD0id2hpdGUiIGZvbnQtd2VpZ2h0PSJib2xkIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiPkI8L3RleHQ+PC9zdmc+Cg==',
            iconSize: [32, 32],
            iconAnchor: [16, 16],
            popupAnchor: [0, -16],
            className: 'drop-shadow-lg',
          })}
        >
          <Popup>
            <div className="text-sm">
              <p className="font-semibold">Arrivée</p>
              <p className="text-gray-600">{formatTimeInBrussels(endPosition.timestamp)}</p>
              <p className="text-xs text-gray-500">
                {endPosition.latitude.toFixed(4)}, {endPosition.longitude.toFixed(4)}
              </p>
            </div>
          </Popup>
        </Marker>

        {/* Intermediate position dots (every 10th position to avoid clutter) */}
        {positions
          .filter((_, index) => index % 10 === 0 && index !== 0 && index !== positions.length - 1)
          .map((position, index) => (
            <Marker
              key={index}
              position={[position.latitude, position.longitude]}
              icon={L.icon({
                iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSI2IiBjeT0iNiIgcj0iMyIgZmlsbD0iIzNiODJmNiIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9zdmc+',
                iconSize: [12, 12],
                iconAnchor: [6, 6],
                popupAnchor: [0, -6],
              })}
            >
              <Popup>
                <div className="text-xs">
                  <p className="text-gray-600">{formatTimeInBrussels(position.timestamp)}</p>
                  <p className="text-gray-500">
                    {position.latitude.toFixed(4)}, {position.longitude.toFixed(4)}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}
      </MapContainer>
    </div>
  );
}
